import { useRef, useState, useEffect } from 'react'
import { motion } from 'framer-motion'

// Three-tier hero video (PERF-012). The browser picks the FIRST <source>
// whose `media` matches the viewport — no JS needed for the swap. Mobile
// stays at 1.2 MB (portrait-friendly framing), the new mid tier covers
// tablets / small laptops (was paying 23 MB on every load), large
// desktops still get the full-res file.
//   ≤ 768px           → /hero-mobile.mp4   (1.2 MB)
//   769 - 1280px      → /hero-mid.mp4      (1.3 MB; was 23 MB before)
//   1281px+           → /hero-background.mp4 (23 MB; future PERF-012b
//                      reduces this to ~11 MB via a separate re-encode)
const DESKTOP_VIDEO_SRC = '/hero-background.mp4'
const MID_VIDEO_SRC = '/hero-mid.mp4'
const MOBILE_VIDEO_SRC = '/hero-mobile.mp4'
// Single high-quality poster used for every breakpoint — shows instantly
// while the matching tier video downloads. Snapshot taken from the loop's
// peak-ray frame at the 2.5s mark.
const POSTER_SRC = '/hero-poster.webp'

// Native <video> hero background on every device. Previously the desktop path
// rendered an R3F <Canvas> with a distortion shader, which reliably swallowed
// wheel/scroll gestures on several browsers — wheel events fired but native
// scroll was blocked because the canvas element set `touch-action: none` and
// re-enabled `pointer-events: auto` inside its wrapper, overriding the
// parent's `pointer-events: none`. Swapping to a plain <video> tag removes
// every path that can absorb scroll.
function HeroVideoBackground({ isVisible }) {
  const videoRef = useRef(null)
  const [videoPlaying, setVideoPlaying] = useState(false)
  const poster = POSTER_SRC

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (isVisible) {
      video.play().catch(() => {})
    } else {
      video.pause()
    }
  }, [isVisible])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    video.muted = true
    video.defaultMuted = true
    video.setAttribute('muted', '')
    video.setAttribute('playsinline', '')
    video.setAttribute('webkit-playsinline', '')
    video.preload = 'auto'
    video.load()

    const tryPlay = () => video.play().catch(() => {})
    const onPlaying = () => setVideoPlaying(true)
    video.addEventListener('playing', onPlaying)

    tryPlay()

    const onLoadedData = () => {
      if (video.paused) tryPlay()
    }
    video.addEventListener('loadeddata', onLoadedData, { once: true })

    const onInteraction = () => {
      if (video.paused) tryPlay()
      window.removeEventListener('scroll', onInteraction)
      window.removeEventListener('touchstart', onInteraction)
    }
    window.addEventListener('scroll', onInteraction, { passive: true })
    window.addEventListener('touchstart', onInteraction, { passive: true })

    const t1 = setTimeout(() => {
      if (video.paused) tryPlay()
    }, 1000)
    const t2 = setTimeout(() => {
      if (video.paused) tryPlay()
    }, 3000)

    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      video.removeEventListener('playing', onPlaying)
      video.removeEventListener('loadeddata', onLoadedData)
      window.removeEventListener('scroll', onInteraction)
      window.removeEventListener('touchstart', onInteraction)
    }
  }, [])

  return (
    <div className="absolute inset-0 w-full h-full bg-[var(--bg-base)] overflow-hidden">
      {/* Full-bleed at native scale across every breakpoint — the smallest
          element that still covers the whole hero. Source video pixels map
          1:1 to display pixels (or with mild cover-driven scaling on
          off-aspect viewports), giving the sharpest possible result. Cross
          lands wherever the source naturally places it (~37% horizontal). */}
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        preload="auto"
        poster={poster}
        className="absolute inset-0 w-full h-full object-cover object-center"
        style={{
          opacity: videoPlaying ? 1 : 0,
          transition: 'opacity 0.8s ease',
        }}
      >
        {/* Three-tier source set (PERF-012). Order matters: browser
            picks the first <source> whose `media` matches. Desktop tier
            currently uses H.264 CRF 25 (~3.9 MB) — picked as the best
            quality-to-size compromise after side-by-side testing against
            AV1 and VP9 (test files retained in /public). */}
        <source src={MOBILE_VIDEO_SRC} type="video/mp4" media="(max-width: 768px)" />
        <source src={MID_VIDEO_SRC} type="video/mp4" media="(max-width: 1280px)" />
        <source src={DESKTOP_VIDEO_SRC} type="video/mp4" />
      </video>
    </div>
  )
}

export default function VideoHero() {
  const heroRef = useRef(null)
  const [isVisible, setIsVisible] = useState(true)
  useEffect(() => {
    const el = heroRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
      threshold: 0,
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={heroRef}
      className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-[var(--bg-base)]"
    >
      {/* Video background — native <video>, pointer-events-none so it can't
          intercept wheel or touch gestures. Text overlay below (z-10)
          handles all interactions. */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <HeroVideoBackground isVisible={isVisible} />
      </div>

      {/* Left-side darkening gradient — boosts text readability over the
          cross/light background on the left half, fades to transparent
          across the cross on the right. Heavier on the left for legibility
          of the smaller sub-line at the bottom of the text block. */}
      <div className="absolute inset-0 z-[5] pointer-events-none bg-gradient-to-r from-[#030312]/95 via-[#030312]/55 to-transparent" />

      {/* Edge-to-edge container with responsive padding identical to the
          navbar (lg:px-12 → 2xl:px-24) so the headline's left edge aligns
          with the logo across every breakpoint. Inner text column capped
          so the headline doesn't grow uncomfortably wide on 2xl monitors. */}
      <div className="relative z-10 w-full px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-24 flex items-center min-h-screen">
        <div className="w-full lg:w-[55%] lg:max-w-[640px] text-center lg:text-left">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.2, 0.65, 0.3, 0.9] }}
            className="font-heading font-extrabold tracking-tight text-white text-5xl md:text-6xl lg:text-7xl leading-[1.05] drop-shadow-[0_4px_40px_rgba(0,0,0,0.85)]"
          >
            Tools for Christians
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.2, 0.65, 0.3, 0.9] }}
            className="mt-5 font-heading tracking-[0.08em] sm:tracking-[0.15em] uppercase text-xs sm:text-sm md:text-base text-white/55 drop-shadow-[0_2px_15px_rgba(0,0,0,0.85)]"
          >
            Scripture · Apologetics · Teaching
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-10 flex justify-center lg:justify-start"
          >
            <a
              href="/login?mode=signup"
              className="rounded-full bg-[var(--gold)] px-10 py-4 font-semibold text-[#1a1a2e] shadow-lg text-lg hover:brightness-110 hover:scale-105 hover:shadow-[0_0_40px_rgba(212,168,67,0.6)] transition-all duration-200 inline-block"
            >
              Try Free
            </a>
          </motion.div>
        </div>
      </div>

      {/* Scroll-down nudge — bottom-center, subtle gold bounce. Fades in
          after the headline + CTA settle so it doesn't compete on first
          paint. Pure visual cue (no click handler) — the page's existing
          scroll behavior carries the user down. */}
      <motion.div
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 pointer-events-none"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-1 text-white/55 drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]"
        >
          <span className="text-[10px] uppercase tracking-[0.25em] font-heading font-semibold">
            See inside
          </span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </motion.div>
      </motion.div>
    </div>
  )
}
